import {
  deletePriorAuthAttachment as sdkDeletePriorAuthAttachment,
  downloadPriorAuthAttachment as sdkDownloadPriorAuthAttachment,
  getPriorAuthDetails,
  listPatientPriorAuths,
  priorAuthAction,
  syncPriorAuths,
} from '@lotushealth/lotushealth_js_sdk/endpoints';
import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import type {
  PriorAuthActionBody,
  PriorAuthActionResponseT,
  PriorAuthCaseDetailsT,
  PriorAuthSummaryT,
  PriorAuthSyncResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus';

export async function fetchPatientPriorAuths(
  patientUserId: string,
  signal?: AbortSignal
): Promise<PriorAuthSummaryT[]> {
  const config = await createLotusRequestConfig({ signal });
  const response = await listPatientPriorAuths(patientUserId, config);
  return lotusHealthZod.listPatientPriorAuthsResponse.parse(response.data) as PriorAuthSummaryT[];
}

export async function fetchPriorAuthDetails(
  recordId: string,
  signal?: AbortSignal
): Promise<PriorAuthCaseDetailsT> {
  const config = await createLotusRequestConfig({ signal });
  const response = await getPriorAuthDetails(recordId, config);
  return lotusHealthZod.getPriorAuthDetailsResponse.parse(response.data) as PriorAuthCaseDetailsT;
}

export async function submitPriorAuthAction(
  caseId: number,
  request: PriorAuthActionBody
): Promise<PriorAuthActionResponseT> {
  const config = await createLotusRequestConfig();
  const response = await priorAuthAction(caseId, request, config);
  return lotusHealthZod.priorAuthActionResponse.parse(response.data) as PriorAuthActionResponseT;
}

export async function triggerPriorAuthSync(patientUserId: string): Promise<PriorAuthSyncResponseT> {
  const config = await createLotusRequestConfig();
  const response = await syncPriorAuths(patientUserId, config);
  return lotusHealthZod.syncPriorAuthsResponse.parse(response.data) as PriorAuthSyncResponseT;
}

/**
 * Stream a PA attachment back from cortex (which proxies DoseSpot) as a
 * ``Blob``. The caller is responsible for handing the blob to the
 * browser as a download (see ``triggerBrowserDownload`` in the wizard);
 * this layer just owns the auth + transport and stays free of DOM
 * concerns so it can be reused from non-UI callers later.
 *
 * The SDK function returns ``AxiosResponse<unknown>`` because the
 * endpoint is documented as ``application/octet-stream`` and we don't
 * want the generator to assume a JSON body. We override
 * ``responseType: 'blob'`` so axios hands us raw bytes instead of
 * trying to parse them as text or JSON.
 */
export async function downloadPriorAuthAttachment(
  caseId: number,
  attachmentId: number,
  signal?: AbortSignal
): Promise<Blob> {
  const config = await createLotusRequestConfig({ signal });
  const response = await sdkDownloadPriorAuthAttachment(caseId, attachmentId, {
    ...config,
    responseType: 'blob',
  });
  return response.data as Blob;
}

/**
 * DELETE the named attachment from a PA case. Resolves on success;
 * rejects with the axios error on non-2xx (DoseSpot may refuse a
 * delete once the case has moved into a payer-owned status, which
 * cortex surfaces as 4xx).
 */
export async function deletePriorAuthAttachment(
  caseId: number,
  attachmentId: number
): Promise<void> {
  const config = await createLotusRequestConfig();
  await sdkDeletePriorAuthAttachment(caseId, attachmentId, config);
}
